import { create } from 'zustand'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export type ToastAction = { label: string; onClick: () => void }

export type Toast = {
  id: string
  type: ToastType
  title: string
  description?: string
  duration: number
  action?: ToastAction
}

export type ToastOptions = {
  description?: string
  duration?: number
  action?: ToastAction
}

const DEFAULT_DURATION = 5000

type StoreState = {
  toasts: Toast[]
}

type StoreActions = {
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  clearAllToasts: () => void
}

let toastCounter = 0

export const useToastStore = create<StoreState & StoreActions>()((set) => ({
  toasts: [],
  addToast: (toast) => {
    const id = String(toastCounter++)
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }))
  },
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }))
  },
  clearAllToasts: () => {
    set({ toasts: [] })
  },
}))

function createToast(type: ToastType, title: string, options?: ToastOptions) {
  useToastStore.getState().addToast({
    type,
    title,
    description: options?.description,
    duration: options?.duration ?? (type === 'error' ? Infinity : DEFAULT_DURATION),
    action: options?.action,
  })
}

export const toast = {
  success: (title: string, options?: ToastOptions) => createToast('success', title, options),
  error: (title: string, options?: ToastOptions) => createToast('error', title, options),
  info: (title: string, options?: ToastOptions) => createToast('info', title, options),
  warning: (title: string, options?: ToastOptions) => createToast('warning', title, options),
}

if (import.meta.env.DEV) {
  ;(window as any).__toast = { ...toast, clear: () => useToastStore.getState().clearAllToasts() }
}

const toastForFlashKey: Record<string, (title: string) => void> = {
  notice: toast.success,
  success: toast.success,
  alert: toast.error,
  danger: toast.error,
  warning: toast.warning,
}

export function toastFlash(flash: Record<string, unknown>) {
  for (const [key, message] of Object.entries(flash)) {
    if (typeof message === 'string' && message) toastForFlashKey[key]?.(message)
  }
}
